// a redux slice to hold patient list filters, visibile columns, sort order and pagination
// needs to be compatible with mui x data grid pro

import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { type RootState } from "@/store";
import { useSelector } from "react-redux";

export interface Filters {
  area: string[];
  status: string[];
}

export interface AdvancedFilters {
  ageRange: string;
  snrDoctor: string;
  doctor: string;
  nurse: string;
}

export interface PatientListState {
  filters: Filters;
  advancedFilters: AdvancedFilters;
  activeView: string | null;
  columns: string[];
  sort: Record<string, unknown>;
  pagination: Record<string, unknown>;
}

export const initialState: PatientListState = {
  filters: {
    area: [],
    status: ["submitted", "triaged", "seen"],
  },
  advancedFilters: {
    ageRange: "",
    snrDoctor: "",
    doctor: "",
    nurse: "",
  },
  activeView: null,
  columns: [],
  sort: {},
  pagination: {},
};

export const patientListSlice = createSlice({
  name: "patientList",
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<Filters>) => {
      state.filters = action.payload;
    },
    setAdvancedFilters: (state, action: PayloadAction<AdvancedFilters>) => {
      state.advancedFilters = action.payload;
    },
    clearAdvancedFilters: (state) => {
      state.advancedFilters = initialState.advancedFilters;
    },
    setActiveView: (state, action: PayloadAction<string | null>) => {
      state.activeView = action.payload;
    },
    setColumns: (state, action: PayloadAction<string[]>) => {
      state.columns = action.payload;
    },
    setSort: (state, action: PayloadAction<Record<string, unknown>>) => {
      state.sort = action.payload;
    },
    setPagination: (state, action: PayloadAction<Record<string, unknown>>) => {
      state.pagination = action.payload;
    },
  },
});

export const {
  setFilters,
  setAdvancedFilters,
  clearAdvancedFilters,
  setActiveView,
  setColumns,
  setSort,
  setPagination,
} = patientListSlice.actions;

export const patientListSelector = (s: RootState) =>
  s.patientList as PatientListState;

export const usePatientList = () => useSelector(patientListSelector);

export default patientListSlice.reducer;
